import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { getSmsCodeSchema } from "@/entities/sms-code-form";
import { useCheckRecoveryMutation, useInitRecoveryMutation } from "@/services/api";
import { useAppDispatch, useAppSelector } from "@/services/store";
import type { SmsCodeFormValues } from "@/entities/sms-code-form";
import {
  passwordRecoveryActions,
  passwordRecoverySelectors,
  startCoolDownTimer,
} from "../password-recovery-slice";
import { PasswordRecoverySteps } from "../types";
import { RESEND_COOL_DOWN } from "../constants";

export const useSmsCodeForm = () => {
  const dispatch = useAppDispatch();

  const resendCoolDown = useAppSelector(passwordRecoverySelectors.selectResendCoolDown);
  const mobilePhone = useAppSelector(passwordRecoverySelectors.selectMobilePhone);

  const [checkCode, { isLoading }] = useCheckRecoveryMutation();
  const [init, { isLoading: resendIsLoading }] = useInitRecoveryMutation();

  const formApi = useForm({
    resolver: yupResolver(getSmsCodeSchema()),
  });

  const handleSubmit = (data: SmsCodeFormValues) => {
    if (!mobilePhone) {
      return;
    }

    checkCode({ ...data, mobilePhone })
      .unwrap()
      .then(() => {
        dispatch(passwordRecoveryActions.clearResendCoolDown());
        dispatch(passwordRecoveryActions.setStep(PasswordRecoverySteps.NEW_PASSWORD_STEP));
      })
      .catch((error) => {
        formApi.setError("code", { message: error.message });
      });
  };

  const onResend = () => {
    if (!mobilePhone) {
      return;
    }

    init({ mobilePhone })
      .unwrap()
      .then(() => {
        dispatch(passwordRecoveryActions.clearResendCoolDown());
        dispatch(startCoolDownTimer(RESEND_COOL_DOWN));
      });
  };

  const submitDisabled = !formApi.formState.isValid;

  return {
    isLoading: isLoading || resendIsLoading,
    onSubmit: handleSubmit,
    submitDisabled,
    formApi,
    resendCoolDown,
    onResend,
  };
};
